import {
  getInspectionDecisionLabel,
  getWorkflowStatusLabel,
  INSPECTION_DECISION_OPTIONS,
} from "../constants";
import { resolvePhotoUrl } from "../photo-storage";
import { getReportTemplateDefaults } from "../report-defaults";
import type { InspectionSubitemData } from "../types";
import { escapeHtml, renderPagePath } from "../view-helpers";

type SubitemSectionSummary = {
  label: string;
  stateLabel: string;
  warning?: boolean;
  detail: string;
};

function buildSubitemSummaries(
  subitem: InspectionSubitemData,
  sampleCount: string,
  sampledLocations: string,
  findings: string,
): SubitemSectionSummary[] {
  const isNotInvolved = subitem.decision === "not_involved";
  const textReady = Boolean(sampleCount.trim() && sampledLocations.trim() && findings.trim());
  const photoReady = subitem.photos.length > 0;
  const decisionReady = subitem.decision !== "pending";

  return [
    {
      label: "文本内容",
      stateLabel: isNotInvolved ? "不涉及" : textReady ? "已填写" : "待补充",
      warning: !isNotInvolved && !textReady,
      detail: isNotInvolved
        ? "当前子项不涉及，抽查数量、部位和情况都不需要再填写。"
        : textReady
          ? "抽查数量、抽查部位和抽查情况都已有内容。"
          : "抽查数量、抽查部位或抽查情况仍有缺项。",
    },
    {
      label: "现场图片",
      stateLabel: isNotInvolved ? "不涉及" : photoReady ? `已上传 ${subitem.photos.length} 张` : "待上传",
      warning: !isNotInvolved && !photoReady,
      detail: isNotInvolved
        ? "当前子项不涉及，不需要上传现场图片。"
        : photoReady
          ? "现场图片已经留存，后续报告附件会带上这些照片。"
          : "现场图片还没有上传，后续报告附件目前不会带图。",
    },
    {
      label: "子项评定",
      stateLabel: decisionReady ? getInspectionDecisionLabel(subitem.decision) : "待填写",
      warning: !decisionReady,
      detail: decisionReady
        ? "当前评定结果已经选定，完成状态会自动跟随更新。"
        : "还没有选择子项评定结果，当前子项还不能算完成。",
    },
  ];
}

function renderPhotoList(subitem: InspectionSubitemData): string {
  if (subitem.decision === "not_involved") {
    return `<p class="muted">该子项不涉及，无需上传图片。</p>`;
  }

  if (!subitem.photos.length) {
    return `<p class="muted">还没有上传现场图片。</p>`;
  }

  return `
    <div class="photo-grid compact-grid">
      ${subitem.photos
        .map(
          (photo, index) => {
            const previewUrl = resolvePhotoUrl(photo);
            return `
            <article
              class="photo-card compact-photo-card reorderable-photo-card"
              data-photo-scope="subitem"
              data-photo-key="${escapeHtml(photo)}"
              data-photo-index="${index}"
            >
              <button
                type="button"
                class="preview-link preview-button"
                data-preview-image="${previewUrl}"
                data-preview-title="${escapeHtml(subitem.subitemName)} 现场图片 ${index + 1}"
              >
                <img src="${previewUrl}" alt="${escapeHtml(subitem.subitemName)} 现场图片 ${index + 1}" />
              </button>
              <button type="button" class="photo-delete-button" data-photo-delete="${index}" aria-label="删除图片">×</button>
            </article>
          `;
          },
        )
        .join("")}
    </div>
  `;
}

export function renderInspectionSubitemEditPage(
  projectName: string,
  projectId: string,
  subitem: InspectionSubitemData,
): string {
  const disabled = subitem.decision === "not_involved" ? "disabled" : "";
  const defaults = getReportTemplateDefaults(subitem.subitemId);
  const sampleCount = subitem.sampleCount || defaults?.sampleCount || "";
  const sampledLocations = subitem.sampledLocations || defaults?.sampledLocations || "";
  const findings = subitem.findings || defaults?.findings || "";

  return `
    <section class="screen">
      <header class="topbar">
        <button class="ghost-button" data-action="back-to-item-detail">返回子项列表</button>
        <h1>子项编辑</h1>
      </header>
      ${renderPagePath([
        { label: projectName, href: `#/projects/home?id=${projectId}` },
        { label: "现场评定", href: `#/projects/inspection-items?id=${projectId}` },
        {
          label: subitem.itemName,
          href: `#/projects/inspection-item?projectId=${projectId}&itemId=${subitem.itemId}`,
        },
        { label: subitem.subitemName },
      ])}
      <div class="panel stack">
        <p class="eyebrow">${escapeHtml(subitem.itemName)}</p>
        <h2>${escapeHtml(subitem.subitemName)}</h2>
        <p class="muted">当前完成状态：${getWorkflowStatusLabel(subitem.workflowStatus)}</p>
        <p class="muted">当前评定结果：${getInspectionDecisionLabel(subitem.decision)}</p>
        <p class="muted">本页已把模板默认文本直接预填到输入框里，你只需要在此基础上修改。完成状态会跟随子项评定自动变化，不需要单独再点按钮。</p>
        ${
          subitem.decision === "not_involved"
            ? `<p class="muted">该子项不涉及，无需填写抽查数量、抽查部位、抽查情况和照片。若要恢复填写，请先把子项评定改回“待填写/合格/不合格”。</p>`
            : ""
        }
      </div>
      <form id="subitem-form" class="panel stack">
        <p class="mini-meta">这 3 个文本字段输入时会自动保存到本地；图片通过下面的“上传图片”入口追加保存。</p>
        <label class="field">
          <span>抽查数量</span>
          <input name="sampleCount" type="text" value="${escapeHtml(sampleCount)}" placeholder="例如：2 处、5 套、全部检查" ${disabled} />
        </label>
        <label class="field">
          <span>具体抽查部位</span>
          <textarea name="sampledLocations" rows="3" placeholder="例如：一层大厅、二层疏散楼梯间" ${disabled}>${escapeHtml(sampledLocations)}</textarea>
        </label>
        <label class="field">
          <span>抽查情况</span>
          <textarea name="findings" rows="5" placeholder="填写现场抽查情况、存在问题或符合情况" ${disabled}>${escapeHtml(findings)}</textarea>
        </label>
        <label class="field">
          <span>子项评定</span>
          <select name="decision" class="select-field">
            ${INSPECTION_DECISION_OPTIONS.map(
              (option) =>
                `<option value="${option.value}" ${
                  option.value === subitem.decision ? "selected" : ""
                }>${option.label}</option>`,
            ).join("")}
          </select>
        </label>
        <div class="field">
          <span>现场图片</span>
          <div class="photo-inputs">
            <label class="upload-chip">
              <span>图片上传</span>
              <input id="photo-gallery-input" name="photos" type="file" accept="image/*" multiple ${disabled} />
            </label>
          </div>
          <p class="mini-meta" id="subitem-photo-upload-status">该入口支持多选；选择图片后会先处理压缩，再自动追加到当前子项里。部分手机也会在这里提供拍照入口。</p>
          ${renderPhotoList(subitem)}
        </div>
        <div class="inline-head">
          <span class="mini-meta">图片要求：涉及项至少一张，不涉及项不传图。</span>
        </div>
        <button class="primary-button" type="submit">保存子项</button>
      </form>
    </section>
  `;
}
